import { useEffect, useRef, useState } from "react";
import { BiMessageRoundedDots } from "react-icons/bi";
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import { useSelector } from "react-redux";
import ConnectionStatusBanner from "../components/ConnectionStatusBanner";
import Loading from "../components/Loading";
import SubscribeModal from "../components/SubscribeModal";
import SubscribePromoInlineCard from "../components/SubscribePromoInlineCard";
import { getChatList, setChatList } from "../localDb/personal_chat";
import Api from "../service/http";
import { useSocket } from "../service/SocketProvider";
import { hasActiveAccess } from "../utils/subscription";
import ChatList from "./chat/ChatList";

const contentCardClass = (theme) =>
  `flex h-full min-h-0 w-full flex-col overflow-hidden rounded-2xl border shadow-xl ${
    theme === "dark"
      ? "border-slate-800 bg-slate-900/40"
      : "border-slate-200 bg-white"
  }`;

const PersonalChats = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isChatListRoute = location.pathname.replace(/\/+$/, "") === "/personal-chat";
  const { theme } = useSelector((state) => state.theme);
  const { user: contextUser } = useOutletContext() || {};
  const user = contextUser || JSON.parse(localStorage.getItem("user"));
  const subscribed = hasActiveAccess(user);
  const [chatLists, setchatLists] = useState([]);
  // "loading" until the server answers once; an empty list after that is not a loading state.
  const [loadState, setLoadState] = useState("loading");
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);

  const { socket, isConnected, subscribe, unsubscribe, connectionVersion } = useSocket();
  const [typingChat, setTypingChat] = useState([]);
  const controlTyping = (chatId, typing) => {
    if (typing) {
      setTypingChat((prevTypingChat) => {
        const ids = prevTypingChat.map((item) => item?.id);
        if (!ids.includes(chatId)) {
          return [
            ...prevTypingChat,
            { id: chatId, time: new Date().getTime() },
          ];
        }
        return prevTypingChat;
      });
    } else {
      setTypingChat((prevTypingChat) =>
        prevTypingChat.filter((item) => item?.id !== chatId)
      );
    }
  };

  // Expire stale typing indicators (was a setTimeout in the render body -> render loop).
  useEffect(() => {
    const interval = setInterval(() => {
      setTypingChat((prevTypingChat) => {
        const next = prevTypingChat.filter((id) => new Date().getTime() - id?.time < 3000);
        return next.length === prevTypingChat.length ? prevTypingChat : next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Update the list from the event instead of re-downloading it for every message.
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const openChatId = () => {
    const match = pathRef.current.match(/\/(\d+)\/?$/);
    return match ? match[1] : null;
  };
  const refetchTimer = useRef(null);
  const scheduleRefetch = () => {
    if (refetchTimer.current) clearTimeout(refetchTimer.current);
    refetchTimer.current = setTimeout(() => {
      refetchTimer.current = null;
      getChatListFromServer();
    }, 800);
  };
  useEffect(() => () => refetchTimer.current && clearTimeout(refetchTimer.current), []);

  const applyMessageEvent = (data) => {
    const message = data?.message;
    const chatId = data?.chat_id ?? message?.chat_id;
    if (!message?.id || !chatId) return;

    setchatLists((prev) => {
      const index = prev.findIndex((c) => c?.id == chatId);
      if (index === -1) {
        // A chat we do not have yet (new chat / newly added) - fetch the list.
        if (data?.isScroll) scheduleRefetch();
        return prev;
      }

      const current = prev[index];
      const isNew = !!data?.isScroll && (!current?.last_msg || message.id > current.last_msg.id);
      if (!isNew) {
        if (current?.last_msg?.id !== message.id) return prev;
        const updated = [...prev];
        updated[index] = { ...current, last_msg: message };
        setChatList(updated);
        return updated;
      }

      const fromMe = message?.user_id == user?.id || message?.sender?.id == user?.id;
      const isOpen = openChatId() == chatId;
      const next = {
        ...current,
        last_msg: message,
        unread_count: fromMe || isOpen ? 0 : (current?.unread_count || 0) + 1,
      };
      const updated = [next, ...prev.slice(0, index), ...prev.slice(index + 1)];
      setChatList(updated);
      return updated;
    });
  };

  // Opening a chat reads it.
  useEffect(() => {
    const id = openChatId();
    if (!id) return;
    setchatLists((prev) =>
      prev.some((c) => c?.id == id && c?.unread_count)
        ? prev.map((c) => (c?.id == id ? { ...c, unread_count: 0 } : c))
        : prev
    );
  }, [location.pathname]);

  // Re-sync after a reconnect.
  const seenConnectionVersion = useRef(connectionVersion);
  useEffect(() => {
    if (connectionVersion === seenConnectionVersion.current) return;
    seenConnectionVersion.current = connectionVersion;
    getChatListFromServer();
  }, [connectionVersion]);

  useEffect(() => {
    const handleMessage = (data) => {
      switch (data.type) {
        case "WebMeesageTyping":
          if (data.chat_id) {
            controlTyping(data?.chat_id, data.typing);
          }
          break;
        case "WebFetchMessage":
          if (data?.chat_type && data.chat_type !== "PR") break;
          applyMessageEvent(data);
          break;
        case "WebFetchChat":
          if (data?.chat_type && data.chat_type !== "PR") break;
          scheduleRefetch();
          break;
        default:
          break;
      }
    };
    if (socket) subscribe(handleMessage);
    return () => unsubscribe(handleMessage);
  }, [socket, subscribe, unsubscribe]);

  const getChatListFromServer = async () => {
    let response = null;
    try {
      response = await Api.get("/web/chats?chat_type=PR");
    } catch (error) {
      console.error("Failed to load chat list:", error);
      setLoadState((prev) => (prev === "loaded" ? prev : "error"));
      return [];
    }
    if (response?.status === 200 && Array.isArray(response.data?.data)) {
      setChatList(response.data?.data);
      setchatLists(response.data?.data);
      setLoadState("loaded");
    } else {
      setLoadState((prev) => (prev === "loaded" ? prev : "error"));
    }
    return response?.data?.data || [];
  };

  const syncChatList = async () => {
    setLoadState((prev) => (prev === "error" ? "loading" : prev));
    const chatList = getChatList();
    setchatLists(chatList);
    await getChatListFromServer();
  };

  useEffect(() => {
    syncChatList();
  }, []);

  useEffect(() => {
    if (location.state?.showSubscribeModal) {
      setShowSubscribeModal(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state?.showSubscribeModal, navigate]);

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden p-3 sm:p-4">
      <div className={contentCardClass(theme)}>
        <ConnectionStatusBanner isConnected={isConnected} />
        {chatLists?.length > 0 || loadState === "loaded" ? (
          <div className="flex h-full min-h-0 w-full overflow-hidden">
            <div
              className={`flex h-full min-h-0 w-full shrink-0 flex-col md:w-[300px] ${
                isChatListRoute ? "flex" : "hidden md:flex"
              } ${theme === "dark" ? "border-gray-700 md:border-r" : "border-gray-200 md:border-r"}`}
            >
              {!subscribed ? (
                <div
                  className={`shrink-0 border-b p-3 ${
                    theme === "dark" ? "border-slate-700" : "border-slate-200"
                  }`}
                >
                  <SubscribePromoInlineCard
                    badgeText="MEMBERS ONLY"
                    badgeIcon="lock"
                    title="Unlock messaging"
                    subtitle="Group & direct chats — subscribe for full access"
                  />
                </div>
              ) : null}
              <div className="min-h-0 flex-1">
                <ChatList
                  chats={chatLists}
                  typingChat={typingChat}
                  type="PR"
                  subscribed={subscribed}
                  onSubscribeRequired={() => setShowSubscribeModal(true)}
                />
              </div>
            </div>
            <div
              className={`min-h-0 min-w-0 flex-1 flex-col bg-inherit ${
                isChatListRoute ? "hidden md:flex" : "flex"
              }`}
            >
              {isChatListRoute ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
                  <BiMessageRoundedDots
                    size={56}
                    className={theme === "dark" ? "text-gray-600" : "text-gray-300"}
                  />
                  <p className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
                    Select a chat to start messaging
                  </p>
                </div>
              ) : subscribed ? (
                <Outlet />
              ) : (
                <Navigate
                  to="/personal-chat"
                  replace
                  state={{ showSubscribeModal: true }}
                />
              )}
            </div>
          </div>
        ) : loadState === "loading" ? (
          <div className="flex flex-1 items-center justify-center">
            <Loading message="Loading Chats..." />
          </div>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
            <BiMessageRoundedDots
              size={56}
              className={theme === "dark" ? "text-gray-600" : "text-gray-300"}
            />
            <p className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
              Couldn't load chats.
            </p>
            <button
              type="button"
              onClick={syncChatList}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              Try again
            </button>
          </div>
        )}
      </div>
      <SubscribeModal
        open={showSubscribeModal}
        onClose={() => setShowSubscribeModal(false)}
        title="Unlock messaging"
        description="Subscribe to read and send messages in group and direct chats."
      />
    </div>
  );
};

export default PersonalChats;
